import React, { useRef } from 'react';
import PropTypes from 'prop-types';
import {
  Alert,
  Button,
  Card,
  CardBody,
  CardHeader,
} from 'reactstrap';
import { SimpleLineIcon } from '../../components/Icons';
import { IMAGE_ACCEPT } from '../../utils/imageHelper';
import styles from './CustomerReviewCreate.module.css';

const getPhotoPreviewClassName = status => {
  if (status === 'uploading') return styles.photoPreviewUploading;
  if (status === 'error') return styles.photoPreviewError;
  return '';
};

const PhotoTile = ({ disabled, onRemove, onRetry, photo }) => (
  <div className={`${styles.photoTile} ${photo.status === 'error' ? styles.photoTileError : ''}`}>
    <div className={`${styles.photoPreview} ${getPhotoPreviewClassName(photo.status)}`}>
      {photo.status === 'success' && (
        <img alt={photo.file.name} loading="lazy" src={photo.previewUrl} />
      )}
      {photo.status !== 'success' && (
        <span
          aria-live="polite"
          className={`${styles.photoStatus} ${photo.status === 'error' ? styles.photoStatusError : ''}`}
          role="status"
        >
          {photo.status === 'error' ? 'Upload failed' : 'Uploading…'}
        </span>
      )}
    </div>
    {photo.status === 'error' && (
      <p className={styles.photoError}>{photo.error}</p>
    )}
    <div className={styles.photoActions}>
      {photo.status === 'error' && (
        <Button
          color="link"
          disabled={disabled}
          onClick={() => onRetry(photo.id)}
          size="sm"
          type="button"
        >
          Retry
        </Button>
      )}
      <Button
        color="link"
        className="text-danger"
        disabled={disabled}
        onClick={() => onRemove(photo.id)}
        size="sm"
        type="button"
      >
        Remove
      </Button>
    </div>
  </div>
);

PhotoTile.propTypes = {
  disabled: PropTypes.bool.isRequired,
  onRemove: PropTypes.func.isRequired,
  onRetry: PropTypes.func.isRequired,
  photo: PropTypes.shape({
    error: PropTypes.string.isRequired,
    file: PropTypes.shape({ name: PropTypes.string.isRequired }).isRequired,
    id: PropTypes.string.isRequired,
    previewUrl: PropTypes.string.isRequired,
    status: PropTypes.oneOf(['uploading', 'success', 'error']).isRequired,
  }).isRequired,
};

const PhotoUploadCard = ({
  alert,
  disabled,
  isSubmitting,
  onFilesSelected,
  onRemove,
  onRetry,
  photos,
}) => {
  const inputRef = useRef(null);
  const uploadingCount = photos.filter(photo => photo.status === 'uploading').length;
  const failedCount = photos.filter(photo => photo.status === 'error').length;
  const keptCount = photos.filter(photo => photo.status === 'success').length;
  const keptMessage = keptCount === 0
    ? '.'
    : keptCount === 1
      ? ' — the other is kept.'
      : ' — the others are kept.';
  const uploadFailureMessage = failedCount === 1
    ? `One upload failed. Remove it and try again${keptMessage}`
    : `${failedCount} uploads failed. Remove them and try again${keptMessage}`;

  const handleInputChange = async event => {
    const files = Array.from(event.target.files || []);
    event.target.value = '';
    await onFilesSelected(files);
  };

  const handleDrop = async event => {
    event.preventDefault();
    if (disabled) return;
    await onFilesSelected(event.dataTransfer.files);
  };

  return (
    <Card>
      <CardHeader>UPLOAD IMAGES</CardHeader>
      <CardBody className={disabled ? styles.disabledUploadCard : ''}>
        {alert && (
          <Alert className={styles.uploadAlert} color={alert.color} isOpen>
            {alert.messages.length === 1 ? alert.messages[0] : (
              <ul className={styles.uploadAlertList}>
                {alert.messages.map(message => <li key={message}>{message}</li>)}
              </ul>
            )}
          </Alert>
        )}

        <input
          accept={IMAGE_ACCEPT}
          aria-label="Select review photos"
          className={styles.fileInput}
          disabled={disabled}
          multiple
          onChange={handleInputChange}
          ref={inputRef}
          type="file"
        />
        <div
          className={styles.dropzoneWrapper}
          onDragOver={event => event.preventDefault()}
          onDrop={handleDrop}
        >
          <button
            aria-describedby="customer-review-upload-help"
            className={styles.dropzone}
            disabled={disabled}
            onClick={() => inputRef.current && inputRef.current.click()}
            type="button"
          >
            <span>{uploadingCount > 0
              ? `Uploading ${uploadingCount} of ${photos.length}…`
              : 'Try dropping your images here, or click to select image to upload.'}</span>
            <span className={styles.plusCircle} aria-hidden="true">
              <SimpleLineIcon iconType="plus" />
            </span>
          </button>
        </div>

        {photos.length > 0 && (
          <div className={styles.photoList}>
            {photos.map(photo => (
              <PhotoTile
                disabled={isSubmitting}
                key={photo.id}
                onRemove={onRemove}
                onRetry={onRetry}
                photo={photo}
              />
            ))}
          </div>
        )}

        {failedCount > 0 && (
          <p aria-live="polite" className={styles.uploadFailureMessage} role="status">
            {uploadFailureMessage}
          </p>
        )}

        <span className={styles.uploadHelp} id="customer-review-upload-help">
          Up to 3 photos · JPEG, PNG or WebP · max 5MB each
        </span>
      </CardBody>
    </Card>
  );
};

PhotoUploadCard.propTypes = {
  alert: PropTypes.shape({
    color: PropTypes.string.isRequired,
    messages: PropTypes.arrayOf(PropTypes.string).isRequired,
  }),
  disabled: PropTypes.bool.isRequired,
  isSubmitting: PropTypes.bool.isRequired,
  onFilesSelected: PropTypes.func.isRequired,
  onRemove: PropTypes.func.isRequired,
  onRetry: PropTypes.func.isRequired,
  photos: PropTypes.arrayOf(PropTypes.object).isRequired,
};

PhotoUploadCard.defaultProps = {
  alert: null,
};

export default PhotoUploadCard;
